import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { Briefing, Condition, MediaCard, captureFrame, FileCard, isBookRoom } from "./MediaRoom";
import "./reviews.css";
import { addNote, decide, editNote, finishReview, getPeople, getReview, getTeam, listReviews, manage, myTimeZone, nudgeReview, sendReview, setDue, setPeople, shareLink, shareStatus, toBase64, type Note, type Review, type ReviewItem, type ReviewSummary } from "./api";
import { answerList, comparePair, draftReply, reviewerCalls, flowOf, frameOf, itemStatus, pageParts, sectionGroups, sectionsOf, versionMix, versionsOf, type Decision } from "./content";
import { HereStrip, MapLightbox, OptionList, SectionIntro } from "./Sections";
import { EmailView, PageView, PlainView, PLATFORMS, SectionList, SectionsView, SocialPost, swapsOf, type Plat } from "./frames";
import { ReviewRows } from "./ReviewCenter";
import { ItemAudio, ReadAlong, type PlayKind } from "./Player";
import { CommentPill, Composer, ItemNotes, WhoContext, useTextPick, type NoteEdits, type Picked, type Who } from "./Notes";
import { ShareWindow } from "@/ui/ShareWindow";
import MobileResponsiveToggle from "@/Apps/Pages/MobileResponsiveToggle";
import { MobileState } from "@/Apps/Pages/MobileResponsiveToggle/types";
import { DecisionFlow, DocButton, Md, QuestionsFlow, type DocLink, type DocOpener } from "./Rooms";
import { LongRoom } from "./LongRoom";
import { FullIcon, MicIcon, NoteIcon, SideIcon, ThemeIcon, useFullscreen, useReviewTheme } from "./shell";

// RV-1 — a review room (Short form first). The locked design: one vertical flow of cards; the
// card in the middle of the screen is "the one you're on" (it lifts and a banner names it);
// a thin progress band with a jump list; tap a card for the compact review tray. A request
// for changes always needs a reason. All AI work happens in Claude; this only shows and records.

const REASONS = ["Check a fact", "Keep the team's version", "Too salesy", "Wrong tone", "Link or contact is wrong", "Not ready to post"];
const LEVEL_TAG: Record<string, string> = { review: "Can review", comment: "Can comment", view: "Can view" };
const BADGE: Record<string, string> = { approved: "Approved", changes: "Changes", ready: "Ready", look: "Needs a look" };

// shared: opened from a share link (/r/<code>) — one review, no sign-in, no list or management
// onOpenDoc: how a shared page (My reviews) opens a linked document room in place
// partOf / onOpenParent: a document room that belongs to another review shows "‹ Part of: …"
// "Our call": a one-line call stays inline; a call written on several lines (Markdown: bold, bullets) is laid out as such
export function RecText({ rec, tail = "" }: { rec: string; tail?: string }) {
  if (!rec.includes("\n")) return <><b>Our call:</b> {rec}{tail}</>;
  return <div className="lr-recmd"><b>Our call</b>{tail}<Md text={rec} /></div>;
}

export function ReviewRoom({ shared = false, backTo, onBack, onOpenDoc, partOf, onOpenParent }: { shared?: boolean; backTo?: string; onBack?: () => void; onOpenDoc?: (d: DocLink) => void; partOf?: { id: string; title: string } | null; onOpenParent?: (id: string) => void } = {}) {
  const params = useParams();
  const location = useLocation();
  // opened from another room's "Open the document": Back returns to that room
  const cameFrom = (location.state as { from?: string } | null)?.from;
  const id = shared ? "shared" : params.id || "";
  const navigate = useNavigate();
  const [theme, toggleTheme] = useReviewTheme();
  const [review, setReview] = useState<Review | null>(null);
  const [removed, setRemoved] = useState<ReviewItem[]>([]);
  const [error, setError] = useState("");
  const [others, setOthers] = useState<ReviewSummary[]>([]);
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [cur, setCur] = useState(0);
  const [plat, setPlat] = useState<Plat>("fb");
  const [view, setView] = useState<string>("");
  // the review list on the left can be tucked away (remembered on this device)
  const [sideOff, setSideOff] = useState(() => { try { return localStorage.getItem("lr-side") === "off"; } catch (e) { return false; } });
  const toggleSide = () => setSideOff((v) => { try { localStorage.setItem("lr-side", v ? "on" : "off"); } catch (e) {} return !v; });
  const [picks, setPicks] = useState<Record<string, Record<string, string>>>({});
  // copy-room pages: each page's version (A by default) and the parts swapped on top of it
  const [bases, setBases] = useState<Record<string, string>>({});
  const baseOf = (x: ReviewItem) => {
    const order = ((review?.meta as { order?: string[] })?.order || Object.keys(x.versions || {})).filter((k) => x.room?.cfgs[k]);
    return bases[x.id] && x.room?.cfgs[bases[x.id]] ? bases[x.id] : order.find((k) => k !== "today") || order[0] || "today";
  };
  const pickPart = (itemId: string, sec: string, key: string) => setPicks((m) => ({ ...m, [itemId]: { ...(m[itemId] || {}), [sec]: key } }));
  const pickBase = (itemId: string, key: string) => { setBases((m) => ({ ...m, [itemId]: key })); setPicks((m) => ({ ...m, [itemId]: {} })); };
  const [device, setDevice] = useState<{ type: MobileState }>({ type: MobileState.DESKTOP });
  const [tray, setTray] = useState(false);
  const [reason, setReason] = useState<{ reasons: string[]; note: string } | null>(null);
  const [jump, setJump] = useState(false);
  const [done, setDone] = useState(false);
  const [flash, setFlash] = useState("");
  const [toast, setToast] = useState("");
  // NR-9: the builder says what happened when it hands back to the room
  useEffect(() => { const t = (location.state as { toast?: string } | null)?.toast; if (t) setToast(t); }, []);
  const [recording, setRecording] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  // NR-1 — when a Can review reviewer clicked Finish
  const [finishedAt, setFinishedAt] = useState("");
  const [composing, setComposing] = useState<(Picked & { frame?: string | null; time?: number }) | null>(null);
  // options on a card: choices tapped but not yet saved (they go with the item's decision)
  const [localChoices, setLocalChoices] = useState<Record<string, (number | null)[]>>({});
  const [mapOpen, setMapOpen] = useState<{ svg: string; title?: string } | null>(null);
  const lrRef = useRef<HTMLDivElement>(null);
  const [picked, clearPicked] = useTextPick(lrRef);
  const [full, toggleFull] = useFullscreen(lrRef, setToast);
  const [player, setPlayer] = useState<{ itemId: string; audio: string; times?: string; label: string } | null>(null);
  // scrolling to the next card stops that card's brief (Short form); a room brief, or audio in a
  // Questions or Decision room, keeps playing while you scroll and read
  const keepAudio = useRef(false);
  keepAudio.current = !!player && (player.itemId === "" || review?.type === "questions" || review?.type === "decision");
  const flowRef = useRef<HTMLDivElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const curRef = useRef(0);
  curRef.current = cur;

  const load = useCallback(async () => {
    try {
      const r = await getReview(id);
      // NR-9: removed items only ever show in the author's results, so the rooms never see them
      setRemoved((r.items || []).filter((it) => it.removed));
      setReview({ ...r, items: (r.items || []).filter((it) => !it.removed) }); setDecisions(r.decisions || {}); setNotes(r.notes || []); setFinishedAt(r.finishedAt || ""); setLocalChoices({}); setError("");
      // section picks saved with an approval ("mix: Headline B, Button A") come back as picks
      const saved: Record<string, Record<string, string>> = {}, savedBase: Record<string, string> = {};
      for (const [itemId, d] of Object.entries(r.decisions || {})) {
        if (!d.version) continue;
        if (!d.version.startsWith("mix: ")) { savedBase[itemId] = d.version; continue; }
        const parts = Object.fromEntries(d.version.slice(5).split(", ").map((p) => { const at = p.lastIndexOf(" "); return [p.slice(0, at), p.slice(at + 1)]; }));
        if (parts.base) { savedBase[itemId] = parts.base; delete parts.base; }
        saved[itemId] = parts;
      }
      setPicks(saved); setBases(savedBase);
      const first = r.items[0];
      const pair = first ? comparePair(first, r.meta) : null;
      setView((r.meta as { defaultView?: string }).defaultView || (pair ? pair[1] : first ? versionsOf(first, r.meta)[0]?.key || "" : ""));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't open this review.");
    }
  }, [id]);
  useEffect(() => { setDone(false); setCur(0); load(); if (!shared) listReviews().then(setOthers).catch(() => {}); }, [id, load]);
  // the share link panel (signed-in only)
  // SH-5 — the Share window: teammates (when the server has sharing) and the no-sign-in link
  const [sharing, setSharing] = useState(false);
  const shareUrl = (t: string) => `${window.location.origin}/r/${t}`;
  const openShare = () => setSharing(true);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(""), 3500); return () => clearTimeout(t); }, [toast]);

  const items = review?.items || [];
  const pending = items.filter((it) => !decisions[it.id]).length;
  const approved = items.filter((it) => decisions[it.id]?.decision === "approve").length;
  const changes = items.length - pending - approved;

  // the sticky card title sits right under the progress band
  useEffect(() => {
    const f = flowRef.current, l = legendRef.current;
    if (f && l) f.style.setProperty("--lh", `${l.offsetHeight}px`);
  });

  // the card filling the middle of the screen is the one you're on
  useEffect(() => {
    const f = flowRef.current;
    if (!f || !items.length || done) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((en) => {
        if (!en.isIntersecting) return;
        const i = Number((en.target as HTMLElement).dataset.i);
        if (i !== curRef.current) { setCur(i); announce(i); }
      }),
      { root: f, rootMargin: "-40% 0px -55% 0px" },
    );
    f.querySelectorAll(".lr-item").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [items.length, done, plat, view]);

  const announce = (i: number) => {
    const it = items[i];
    if (!it) return;
    setFlash(`${it.n ?? i + 1} of ${items.length} · ${it.title || it.id}`);
    window.clearTimeout((announce as unknown as { t?: number }).t);
    (announce as unknown as { t?: number }).t = window.setTimeout(() => setFlash(""), 1100);
    if (!keepAudio.current) stopBrief();
  };

  const scrollTo = (i: number) => {
    const f = flowRef.current, el = f?.querySelector(`[data-i="${i}"]`) as HTMLElement | null;
    if (f && el) f.scrollTo({ top: el.offsetTop - (legendRef.current?.offsetHeight || 56) - 8, behavior: "smooth" });
    setCur(i); announce(i);
  };

  const stopBrief = () => setPlayer(null);
  // the brief, or the item read aloud, in the read-along player (every room type uses this one player)
  const playBrief = (it: ReviewItem, kind: PlayKind = "brief") => {
    const audio = kind === "brief" ? it.briefAudio : it.readAudio;
    if (!audio || !review) return;
    if (player?.audio === audio) return stopBrief();
    setPlayer({ itemId: it.id, audio, times: kind === "brief" ? it.briefTimes : it.readTimes, label: `${kind === "brief" ? "Brief" : "Read aloud"} · ${it.n ? `${it.n}. ` : ""}${it.title || it.id}` });
  };
  // the room's own brief (meta.briefAudio): what this whole review is about
  const roomBrief = (review?.meta as { briefAudio?: string; briefTimes?: string; audioMaking?: boolean } | undefined) || {};
  // NR-12: the server is making the spoken briefs for a review built in the app; look again until they're in
  useEffect(() => {
    if (!roomBrief.audioMaking) return;
    const t = setInterval(() => { load(); }, 8000);
    return () => clearInterval(t);
  }, [roomBrief.audioMaking, load]);
  const playRoomBrief = () => {
    if (!roomBrief.briefAudio || !review) return;
    if (player?.audio === roomBrief.briefAudio) return stopBrief();
    setPlayer({ itemId: "", audio: roomBrief.briefAudio, times: roomBrief.briefTimes, label: `Room brief · ${review.title}` });
  };

  // media room: a comment is about the moment the video is paused on: stop it and take the still
  const grab = (itemId: string): { frame?: string | null; time?: number } => {
    if (review?.type !== "media") return {};
    const v = document.querySelector<HTMLVideoElement>(`video[data-item="${itemId}"]`);
    if (!v) return {};
    v.pause();
    return { frame: captureFrame(v), time: v.currentTime };
  };
  // highlight to comment: the note is pinned to the selected words (and the version on screen)
  const saveComment = async (text: string) => {
    if (!review || !composing) return;
    try {
      const n = await addNote(review.id, { itemId: composing.itemId, ...(composing.quote ? { quote: composing.quote } : {}), ...(composing.anchor ? { anchor: composing.anchor } : {}), ...(composing.frame && composing.time != null ? { frame: composing.frame, time: composing.time } : {}), text, ...(view && view !== "changes" ? { version: view } : {}) });
      setNotes((m) => [...m, n]); setComposing(null); window.getSelection()?.removeAllRanges();
      setToast(n.notMentioned?.length ? `Comment saved. ${n.notMentioned.join(" and ")} can't open this review, so they didn't get an email. Share it with them first.` : "Comment saved");
    } catch (e) { setToast(e instanceof Error ? e.message : "Couldn't save the comment."); }
  };
  // a copy-room page's approval: its version, plus any swapped parts ("mix: base A, Headline B")
  const roomVersion = (x: ReviewItem) => {
    const base = baseOf(x), swaps = swapsOf(picks[x.id] || {}, base);
    return Object.keys(swaps).length ? `mix: base ${base}, ${Object.entries(swaps).map(([k, v]) => `${k} ${v}`).join(", ")}` : base;
  };
  const playNote = (n: Note) => n.file && setPlayer({ itemId: n.itemId, audio: n.file, label: `Voice note${n.quote ? ` on “${n.quote.slice(0, 40)}”` : ""}` });
  const noteEdits: NoteEdits = {
    onEdit: async (n, text) => {
      try { const e = await editNote(review!.id, n.id, { text }); setNotes((m) => m.map((x) => (x.id === n.id ? e : x))); setToast("Note updated"); return true; }
      catch (e) { setToast(e instanceof Error ? e.message : "Couldn't update the note."); return false; }
    },
    onDelete: async (n) => {
      try { await editNote(review!.id, n.id, { delete: true }); setNotes((m) => m.filter((x) => x.id !== n.id)); setToast("Note deleted"); return true; }
      catch (e) { setToast(e instanceof Error ? e.message : "Couldn't delete the note."); return false; }
    },
  };
  // a frame comment jumps its video to the moment it was left
  const seekTo = (n: Note) => {
    const v = document.querySelector<HTMLVideoElement>(`video[data-item="${n.itemId}"]`);
    if (v) { v.currentTime = Number((n.anchor || "").replace(/^t=/, "")) || 0; v.pause(); v.scrollIntoView({ block: "center", behavior: "smooth" }); }
  };
  const notesFor = (itemId: string) => <ItemNotes notes={notes.filter((n) => n.itemId === itemId)} onPlay={playNote} edits={noteEdits} onOpen={review?.type === "media" ? seekTo : undefined} reviewId={review?.id} />;

  // the item's choices as they stand: tapped, else saved, else none (= the recommended stands)
  const choicesFor = (itemId: string, local = localChoices[itemId]) => {
    const x = items.find((y) => y.id === itemId);
    if (!x?.options?.length) return undefined;
    const saved = decisions[itemId]?.choices;
    return x.options.map((_, k) => local?.[k] ?? saved?.[k] ?? null);
  };
  // stay: a choice changed on an item already decided — save it again without moving on
  // NR-8: the author can move the due date after Send, and nudge whoever's overdue
  const moveDue = async (date: string) => {
    if (!review) return;
    try {
      const out = await setDue(review.id, date ? { date, tz: myTimeZone() } : null);
      setReview({ ...review, due: out.due || undefined, dueLabel: out.dueLabel, overdue: out.overdue });
      setToast(date ? "Due date saved." : "Due date removed.");
    } catch (e) { setToast(e instanceof Error ? e.message : "Couldn't save the due date."); }
  };
  const nudge = async () => {
    if (!review) return;
    try { const { nudged } = await nudgeReview(review.id); setToast(`Reminder sent to ${nudged.join(" and ")}.`); }
    catch (e) { setToast(e instanceof Error ? e.message : "Couldn't send the reminder."); }
  };
  const finish = async () => {
    if (!review) return;
    try { const r = await finishReview(review.id); setFinishedAt(r.finishedAt); setToast("Done. The author has been told."); }
    catch (e) { setToast(e instanceof Error ? e.message : "Couldn't send that. Try again."); }
  };
  const send = async (itemId: string, d0: Decision, { stay = false, local }: { stay?: boolean; local?: (number | null)[] } = {}) => {
    if (!review) return;
    const before = decisions[itemId];
    const choices = choicesFor(itemId, local ?? localChoices[itemId]);
    const d: Decision = { ...d0, ...(choices ? { choices } : {}) };
    setDecisions((m) => ({ ...m, [itemId]: d }));
    if (!stay) { setTray(false); setReason(null); }
    try {
      const short = review.type !== "questions" && review.type !== "decision";
      await decide(review.id, { itemId, decision: d.decision, reasons: d.reasons, note: d.note, answer: d.answer, ...(choices ? { choices } : {}), version: !short ? undefined : stay ? before?.version : items.find((x) => x.id === itemId)?.room ? roomVersion(items.find((x) => x.id === itemId)!) : view !== "changes" ? view : comparePair(items.find((x) => x.id === itemId)!, review.meta)?.[1] });
      setLocalChoices((m) => { const n = { ...m }; delete n[itemId]; return n; });
      if (stay) { setToast("Choice saved"); return; }
      if (short) {
        const next = items.findIndex((x, i) => i > cur && !decisions[x.id] && x.id !== itemId);
        const any = items.findIndex((x) => !decisions[x.id] && x.id !== itemId);
        const to = next >= 0 ? next : any;
        if (to >= 0) window.setTimeout(() => scrollTo(to), 300);
      }
      setToast({ approve: review.type === "questions" ? "Took the recommendation" : "Approved", change: review.type === "decision" ? "Disapproved" : "Sent back for changes", reject: "Rejected", question: "Question sent", answer: "Answer saved" }[d.decision]);
      listReviews().then(setOthers).catch(() => {});
    } catch (e) {
      setDecisions((m) => { const n = { ...m }; if (before) n[itemId] = before; else delete n[itemId]; return n; });
      setToast(e instanceof Error ? e.message : "Couldn't save that. Try again.");
    }
  };

  // tapping a choice: held until the item is decided; on an item already decided it's saved right away
  const pickChoice = (itemId: string, k: number, j: number) => {
    const x = items.find((y) => y.id === itemId);
    const cur = choicesFor(itemId) || [];
    const local = (x?.options || []).map((_, i) => (i === k ? j : cur[i] ?? null));
    setLocalChoices((m) => ({ ...m, [itemId]: local }));
    const d = decisions[itemId];
    if (d) send(itemId, d, { stay: true, local });
  };
  const optionsFor = (x: ReviewItem) => (
    <OptionList options={x.options} saved={decisions[x.id]?.choices} local={localChoices[x.id]} onPick={(k, j) => pickChoice(x.id, k, j)}
      hint={localChoices[x.id] && !decisions[x.id] ? "Your choice is saved when you approve or ask for changes." : undefined} />
  );
  const openMap = (title?: string) => (svg: string) => setMapOpen({ svg, title });
  const hereFor = (x: ReviewItem) => <>{x.lookAgain && <div className="lr-lookagain">{lookAgainText}</div>}<HereStrip here={x.here} onOpenMap={openMap(x.here?.path || x.title)} /></>;
  const groups = useMemo(() => (review?.sections?.length ? sectionGroups(items, review.sections) : []), [items, review?.sections]);

  const toggleMic = async (forId?: string, quote?: string, anchor?: string, shot?: { frame?: string | null; time?: number }) => {
    if (!review) return;
    if (recording) { recRef.current?.stop(); return; }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") return setToast("This browser can't record. Type a note instead.");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks: Blob[] = [];
      const itemId = forId || items[cur]?.id;
      const still = shot || grab(itemId);
      rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        try { const n = await addNote(review.id, { itemId, audio: await toBase64(blob), audioType: blob.type, ...(quote ? { quote } : {}), ...(anchor ? { anchor } : {}), ...(still.frame && still.time != null ? { frame: still.frame, time: still.time } : {}) }); setNotes((m) => [...m, n]); setComposing(null); setToast("Voice note saved"); }
        catch (e) { setToast("Couldn't save the voice note."); }
      };
      recRef.current = rec; rec.start(); setRecording(true);
    } catch (e) { setToast("Microphone access was blocked."); }
  };

  const mix = review ? versionMix(items, decisions) : [];
  const vlabel = (k: string) => (review?.meta as { labels?: Record<string, string> } | undefined)?.labels?.[k] || k;
  const mixLine = mix.length ? `Mixed versions: ${mix.map((g) => `${g.items.length === 1 ? "item" : "items"} ${g.items.join(", ")} from ${vlabel(g.version)}`).join("; ")}. That's fine; just check they read well together.` : "";
  const it = items[cur];
  const versions = it ? versionsOf(it, review?.meta) : [];
  const pair = it && review ? comparePair(it, review.meta) : null;
  const social = useMemo(() => items.some((x) => Object.values(x.versions || {}).some((v) => sectionsOf(v.body))), [items]);

  const body = (x: ReviewItem) => {
    if (review?.type === "media") return (
      <>
        <Condition item={x} />
        <MediaCard item={x} reviewId={review.id} />
      </>
    );
    if (review?.type === "long" && x.media) return <FileCard item={x} reviewId={review.id} />;
    const vs = x.versions || {};
    const p = review ? comparePair(x, review.meta) : null;
    const showKey = view === "changes" ? p?.[1] : vs[view] ? view : versionsOf(x, review?.meta)[0]?.key;
    const v = (showKey && vs[showKey]) || {};
    const base = view === "changes" && p ? vs[p[0]] : undefined;
    const frame = frameOf(v);
    if (review?.type === "page" && x.room && view !== "changes") {
      const meta = review.meta as { order?: string[]; labels?: Record<string, string> };
      return <SectionsView item={x} order={meta.order || Object.keys(vs)} labels={meta.labels || {}} base={baseOf(x)} picks={picks[x.id] || {}} device={device} url={x.date} onBase={(k) => pickBase(x.id, k)} onPick={(sec, key) => pickPart(x.id, sec, key)} onReset={() => setPicks((m) => ({ ...m, [x.id]: {} }))} />;
    }
    if (review?.type === "page") {
      const pp = pageParts(v.body);
      if ((v as { html?: string }).html || pp.audience || pp.headline || pp.email) return <PageView v={v} base={base} site={review.client || ""} label={x.date} device={device} />;
    }
    if (frame === "social") return <SocialPost plat={plat} v={v} base={base} kind={x.kind} handle={(review?.client || "account").toLowerCase().replace(/[^a-z0-9]/g, "")} name={review?.client || ""} date={x.date} />;
    if (frame === "email") return <EmailView v={v} base={base} />;
    return <PlainView v={v} base={base} />;
  };

  if (error) return <div className="lr" data-theme={theme}><header className="lr-hdr"><Link className="back" to="/reviews" aria-label="Back to the Review Center">‹</Link><div className="grow"><h1>Review</h1></div></header><div className="lr-error">{error}</div></div>;
  if (!review) return <div className="lr" data-theme={theme}><div className="lr-empty"><span>Loading the review…</span></div></div>;

  const answers = review.type === "questions" || review.type === "decision";
  // a linked document room: signed in, it opens at /reviews/<id>; in My reviews, in place; on a
  // share link (/r/), through the document's own share link, or not at all when it has none
  const openDoc: DocOpener = (d) =>
    !shared ? () => navigate(`/reviews/${encodeURIComponent(d.id)}`, { state: { from: location.pathname } })
      : onOpenDoc ? () => onOpenDoc(d)
        : d.share ? () => window.location.assign(d.share!) : null;
  // nested reviews: the review this document room belongs to (signed in: from the list; My reviews passes it)
  const parent = partOf || (!shared ? others.find((r) => r.id === review.id)?.partOf : null) || null;
  const openParent = parent && (!shared ? () => navigate(`/reviews/${encodeURIComponent(parent.id)}`) : onOpenParent ? () => onOpenParent(parent.id) : null);
  const replyText = answers ? answerList({ ...review, decisions }) : draftReply({ ...review, decisions });
  // SH-1..SH-3 — a server with sharing says who you are and what you may do; without it, as before
  const who: Who | null = review.access ? { people: review.people || {}, me: review.me || null, owner: review.access.level === "owner" } : null;
  // NR-2: a draft opens as a preview — the room as a reviewer will see it, with deciding and sharing off
  const preview = !shared && !!review.draft;
  const canDecide = !preview && review.access?.canDecide !== false;
  const canShare = !shared && !preview && review.access?.canShare !== false;
  const sendDraft = async () => {
    try { await sendReview(review.id); setToast("Sent. Your reviewers have been emailed."); load(); }
    catch (e) { setToast(e instanceof Error ? e.message : "Couldn't send. Try again."); }
  };
  // SH-7: anyone with the no-sign-in link can approve, so only the review's owner may turn it on
  const ownsReview = !review.access || review.access.level === "owner";
  const lookAgainText = ownsReview ? "Changed — waiting on new answers" : "Changed — look again";
  const reviewerList = Object.values(review.reviewers || {});
  // what each reviewer said, for the author only. PRD story 41: side by side, on the item itself.
  const callsFor = (x: ReviewItem) => ownsReview && reviewerList.length > 0 && (
    <ul className="lr-calls" aria-label="Reviewers' calls">
      {reviewerCalls(review.reviewers, review.people, x.id).map((c) => (
        <li key={c.email} className={c.decision || "none"}><span className="who">{c.name}</span><span className="call">{answers && c.decision === "approve" && x.rec ? `Took rec: ${x.rec}` : c.note ? `${c.label}: ${c.note}` : c.label}</span></li>
      ))}
    </ul>
  );
  const noteCount = (itemId: string) => notes.filter((n) => n.itemId === itemId).length;
  const avatar = (review.meta?.avatar || review.client || "LO").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <WhoContext.Provider value={who}>
    <div className={`lr ${shared ? "shared" : ""} ${canDecide ? "" : "nodecide"}`} data-theme={theme} ref={lrRef}>
      <header className="lr-hdr">
        {!shared && <Link className="back" to={cameFrom || "/reviews"} aria-label={cameFrom ? "Back to the question" : "Back to the Review Center"}>‹</Link>}
        {shared && backTo && !onBack && <Link className="back" to={cameFrom || backTo} aria-label={cameFrom ? "Back to the question" : "Back to your reviews"}>‹</Link>}
        {shared && onBack && <button className="back" onClick={onBack} aria-label="Back to your reviews">‹</button>}
        {!shared && <button className="icon sidetoggle" onClick={toggleSide} aria-label={sideOff ? "Show the review list" : "Hide the review list"} aria-pressed={!sideOff} title={sideOff ? "Show the review list" : "Hide the review list"}><SideIcon /></button>}
        <div className="grow">
          {parent && openParent && <button className="lr-partof" onClick={openParent}>‹ Part of: {parent.title}</button>}
          <h1>{review.title}</h1>
          <div className="sub">{[review.client, review.source?.sender && `from ${review.source.sender}`].filter(Boolean).join(" · ")} · <b>{pending} of {items.length}</b> pending</div>
        </div>
        {roomBrief.audioMaking && <span className="lr-making" role="status">Making the audio…</span>}
        {roomBrief.briefAudio && <button className="lr-listen lr-roombrief" onClick={playRoomBrief}>{player?.audio === roomBrief.briefAudio ? "❚❚ Room brief" : "▶ Room brief"}</button>}
        {review.type === "page" && <div className="lr-devices"><MobileResponsiveToggle {...({ onChange: setDevice, mobileState: device, setMobileState: setDevice } as any)} /></div>}
        {!shared && !preview && ownsReview && review.sentAt && <Link className="lr-sharebtn" to={`/reviews/${encodeURIComponent(review.id)}/edit`}>Edit</Link>}
        {canShare && <button className="lr-sharebtn" onClick={openShare} aria-haspopup="dialog">Share</button>}
        {!shared && !canShare && review.access && <span className="lr-sharedtag" title="Shared with you">{LEVEL_TAG[review.access.level] || "Can comment"}</span>}
        {!shared && review.access?.level === "review" && (finishedAt ? <span className="lr-finished" title="The author has been told">Finished</span> : <button className="lr-finishbtn" onClick={finish} title="Tell the author you're done">Finish</button>)}
        {!shared && ownsReview && reviewerList.length > 0 && <span className="lr-finishcount">{reviewerList.filter((r) => r.finishedAt).length} of {reviewerList.length} reviewers finished</span>}
        {!shared && !preview && !review.draft && review.dueLabel === "today" && <span className="lr-duetag">Due today</span>}
        {!shared && !preview && !review.draft && review.dueLabel === "overdue" && (review.overdue?.length
          ? <span className="lr-overdue">Overdue: {review.overdue.join(", ")} <button onClick={nudge}>Nudge</button></span>
          : <span className="lr-overdue">Overdue</span>)}
        {!shared && !preview && !review.draft && ownsReview && <label className="lr-duepick">Due <input type="date" aria-label="Due date" value={review.due?.date || ""} onChange={(e) => moveDue(e.target.value)} /></label>}
        <button className="icon" onClick={toggleFull} aria-pressed={full} aria-label={full ? "Leave full screen (F)" : "Full screen (F)"} title={full ? "Leave full screen (F or Esc)" : "Full screen (F)"}><FullIcon on={full} /></button>
        <button className="icon" onClick={toggleTheme} aria-label={theme === "dark" ? "Light mode" : "Dark mode"}><ThemeIcon theme={theme} /></button>
      </header>
      {(review.type === "media" || review.type === "long") && items.some((x) => x.lookAgain) && <div className="lr-lookagain">{lookAgainText}: {items.filter((x) => x.lookAgain).map((x) => x.title || x.id).join(", ")}</div>}
      {ownsReview && removed.length > 0 && (
        <details className="lr-removed" aria-label="Removed after sending">
          <summary>Removed after sending · {removed.length}</summary>
          {removed.map((x) => (
            <div key={x.id} className="lr-removeditem">
              <div><span className="lr-removedtag">Removed</span> <b>{x.title || x.id}</b></div>
              {callsFor(x)}
            </div>
          ))}
        </details>
      )}
      {preview && (
        <div className="lr-preview" role="status">
          <span><b>Preview.</b> This is what your reviewers will see. Nobody has been told yet.</span>
          <Link to={`/reviews/${encodeURIComponent(review.id)}/edit`}>Back to editing</Link>
          <button onClick={sendDraft}>Send</button>
        </div>
      )}
      <div className={`lr-body ${tray || reason || jump ? "tray-open" : ""} ${sideOff ? "side-off" : ""}`}>
        <aside className="lr-side" aria-label="Other reviews" hidden={shared}>
          <ReviewRows reviews={others.filter((r) => r.status === "open")} tab="open" activeId={review.id} compact onChanged={() => listReviews().then(setOthers).catch(() => {})} />
        </aside>
        {groups.length > 0 && !answers && !isBookRoom(review) && !done && (
          <nav className="lr-outline" aria-label="This review's cards">
            {groups.map((g, gi) => (
              <div key={g.section?.id || `loose-${gi}`}>
                <div className="lr-outsec">{g.section?.title || "Not in a section"}</div>
                {g.items.map(({ item: x, i }) => {
                  const st = itemStatus(x, decisions[x.id]);
                  return <button key={x.id} className={`${i === cur ? "cur" : ""} ${st}`} onClick={() => scrollTo(i)}><span className="n">{x.n ?? i + 1}</span><span className="tt">{x.title || x.id}</span></button>;
                })}
              </div>
            ))}
          </nav>
        )}
        {done ? (
          <div className="lr-flow">
            <div className="lr-done">
              <h2>Review summary</h2>
              <div className="lr-tally"><div><b>{approved}</b>Approved</div><div><b>{changes}</b>Changes</div><div><b>{pending}</b>Still open</div></div>
              {mixLine && <div className="lr-mix">{mixLine}</div>}
              <div className="lr-reply"><small>{answers ? "Decisions to send back" : `Drafted reply${review.source?.channel ? ` · ${review.source.channel}` : ""}`}</small>{replyText}</div>
              <button className="lr-primary" onClick={() => { navigator.clipboard?.writeText(replyText).then(() => setToast("Reply copied"), () => setToast("Select the reply to copy it")); }}>{answers ? "Copy decisions" : "Copy reply"}</button>
              <p style={{ fontSize: 12, color: "var(--muted)", margin: 0 }}>{answers ? <>Claude reads these back and applies them when you say “apply {review.title}.”</> : <>To post it in the original thread, tell Claude “send back {review.title}.” Nothing is posted without your yes.</>}</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="lr-pill" onClick={() => setDone(false)}>Back to the review</button>
                {!shared && <button className="lr-pill" onClick={async () => { await manage(review.id, { action: "done" }); navigate("/reviews"); }}>Mark review done</button>}
              </div>
            </div>
          </div>
        ) : isBookRoom(review) ? (
          <LongRoom review={review} decisions={decisions} notes={notes} onDecide={send} canDecide={canDecide} toast={setToast} onPlayNote={playNote} noteEdits={noteEdits}
            onMoveNote={async (id, anchor, quote) => { try { const e = await editNote(review.id, id, { anchor, quote }); setNotes((m) => m.map((x) => (x.id === id ? e : x))); setToast("Note moved"); } catch (e) { setToast(e instanceof Error ? e.message : "Couldn't move the note."); setNotes((m) => [...m]); } }}
            onAskChanges={(itemId) => { setCur(Math.max(0, items.findIndex((x) => x.id === itemId))); setReason({ reasons: [], note: "" }); }}
            onComment={(c) => setComposing({ quote: c.quote, itemId: c.itemId, anchor: c.anchor, x: 0, y: 0, touch: false })} />
        ) : (
          <div className={`lr-flow ${review.type === "page" ? "wide" : ""} ${review.type === "media" ? "media" : ""}`} ref={flowRef}>
            <div className="lr-legend" ref={legendRef}>
              <div className="lr-band">
                <div className="lr-segs">
                  {items.map((x, i) => (
                    <button key={x.id} tabIndex={-1} aria-label={`Item ${x.n ?? i + 1}`} data-tip={`${x.n ?? i + 1}. ${x.title || x.id} · ${BADGE[itemStatus(x, decisions[x.id])]}`} className={`${decisions[x.id]?.decision === "approve" ? "ok" : decisions[x.id] ? "no" : ""} ${i === cur ? "cur" : ""} ${i < items.length / 3 ? "tl" : i > (items.length * 2) / 3 ? "tr" : ""}`} onClick={() => scrollTo(i)} />
                  ))}
                </div>
                <div className="lr-keys">
                  <span><i style={{ background: "var(--approve)" }} />{approved}</span>
                  <span><i style={{ background: "var(--change)" }} />{changes}</span>
                  <span><i style={{ background: "var(--line)" }} />{pending} to go</span>
                  {review.type === "page" && it && (
                    <span className="lr-nowtitle" aria-live="polite"><b>{it.n ?? cur + 1}. {it.title || it.id}</b><span className={`lr-badge ${itemStatus(it, decisions[it.id])}`}>{BADGE[itemStatus(it, decisions[it.id])]}</span></span>
                  )}
                  <button className="now" onClick={() => setJump(true)} aria-label="Jump to an item">{(it?.n ?? cur + 1)} of {items.length} ▾</button>
                </div>
              </div>
            </div>
            {answers ? (
              review.type === "decision"
                ? <DecisionFlow review={review} decisions={decisions} hereFor={hereFor} optionsFor={optionsFor} callsFor={callsFor} avatar={avatar} recording={recording} onDecide={send} onMic={(id) => toggleMic(id)} notesFor={notesFor} nowPlaying={player?.audio || null} onPlay={playBrief} openDoc={openDoc} />
                : <QuestionsFlow review={review} decisions={decisions} hereFor={hereFor} optionsFor={optionsFor} callsFor={callsFor} avatar={avatar} recording={recording} onDecide={send} onMic={(id) => toggleMic(id)} notesFor={notesFor} nowPlaying={player?.audio || null} onPlay={playBrief} openDoc={openDoc} />
            ) : (
            <div className="lr-items">
              {review.type === "media" && <Briefing review={review} />}
              {flowOf(items, review.sections).map((e) => {
                if (e.kind === "intro") return <SectionIntro key={`s:${e.section.id}`} section={e.section} onOpenMap={openMap(e.section.title)} notes={notesFor(`section:${e.section.id}`)} />;
                const { item: x, i } = e;
                const st = itemStatus(x, decisions[x.id]);
                const isSocial = !!Object.values(x.versions || {}).some((v) => sectionsOf(v.body));
                return (
                  <section key={x.id} className={`lr-item ${i === cur ? "active" : ""} ${decisions[x.id] ? "decided" : ""}`} data-i={i} data-item={x.id}>
                    <button className="lr-ihead" onClick={() => { setCur(i); setTray(true); }} aria-label={`Review options for ${x.title || x.id}`} title="Open the review options">
                      <span className="lr-av">{avatar}</span>
                      <span className="tx"><span className="t">{x.n ?? i + 1}. {x.title || x.id}</span><span className="s2">{[review.client, x.date, x.kind].filter(Boolean).join(" · ")}</span></span>
                      <span className={`lr-badge ${st}`}>{BADGE[st]}</span>
                      {/* SH-10: always say the bar opens the comments panel, not only on hover */}
                      <span className="lr-open" aria-hidden="true"><NoteIcon />{canDecide ? "Comment or decide" : "Comment"}{noteCount(x.id) ? ` · ${noteCount(x.id)}` : ""} ›</span>
                    </button>
                    <div onClick={() => { if ((window.getSelection()?.toString() || "").trim().length >= 3) return; setCur(i); setTray(true); }}>
                      {hereFor(x)}
                      {isSocial && <div className="lr-platline"><i>{PLATFORMS.find((p) => p.key === plat)?.badge}</i>{PLATFORMS.find((p) => p.key === plat)?.label} preview</div>}
                      {body(x)}
                      {optionsFor(x)}
                      {(x.rec || (x.facts && x.facts.length)) && (
                        <div className="lr-callbox">
                          {x.rec && <div className="lr-call"><RecText rec={x.rec} /></div>}
                          {(x.facts || []).slice(0, 3).map((f, k) => <div className="lr-fact" key={k}>Check: {f}</div>)}
                        </div>
                      )}
                      {x.doc && <div className="lr-docrow"><DocButton doc={x.doc} open={openDoc} /></div>}
                      {notesFor(x.id)}
                    </div>
                  </section>
                );
              })}
              <div className="lr-end">
                {pending ? <><b>That's the last item.</b><span>{pending} still to decide. Tap “{items.length ? `${it?.n ?? cur + 1} of ${items.length}` : ""} ▾” to jump to one.</span></> : <b>All {items.length} decided.</b>}
                <button className="lr-primary" onClick={() => { stopBrief(); setDone(true); }}>See the summary and reply</button>
              </div>
            </div>
            )}
            {answers && (
              <div className="lr-end">
                {pending ? <b>{pending} still to decide.</b> : <b>All {items.length} decided.</b>}
                <button className="lr-primary" onClick={() => setDone(true)}>See the decisions to send back</button>
              </div>
            )}
            <div className={`lr-flash ${flash ? "show" : ""}`} aria-live="polite">{flash}</div>
            {!tray && !answers && <div className="lr-tapnote">Tap an item for review options · select words to comment · keep scrolling for the next</div>}
          </div>
        )}
      </div>

      {tray && it && !reason && (
        <div className="lr-ovl" onClick={(e) => e.target === e.currentTarget && setTray(false)}>
          <div className="lr-tray" role="dialog" aria-label={`Review options for ${it.title || it.id}`}>
            <div className="top"><b>{it.n ?? cur + 1}. {it.title || it.id}</b><button onClick={() => setTray(false)} aria-label="Close">✕</button></div>
            <ItemAudio it={it} now={player?.audio || null} onPlay={playBrief} />
            <div className="lr-segrow">
              {social && <div className="lr-seg" role="group" aria-label="Show it on">{PLATFORMS.map((p) => <button key={p.key} aria-pressed={plat === p.key} onClick={() => setPlat(p.key)}>{p.label}</button>)}</div>}
              <div className="lr-seg" role="group" aria-label="Version">
                {versions.map((v) => <button key={v.key} aria-pressed={it.room ? baseOf(it) === v.key && view !== "changes" : view === v.key} onClick={() => { if (it.room) { pickBase(it.id, v.key); if (view === "changes") setView(v.key); } else setView(v.key); }}>{v.label}</button>)}
                {pair && <button aria-pressed={view === "changes"} onClick={() => setView("changes")}>Changes</button>}
              </div>
            </div>
            {mixLine && <div className="lr-mix">{mixLine}</div>}
            {it.room && view !== "changes" && <SectionList item={it} order={((review.meta as { order?: string[] }).order || Object.keys(it.versions || {}))} base={baseOf(it)} picks={picks[it.id] || {}} onPick={(sec, key) => pickPart(it.id, sec, key)} />}
            <div className="lr-tip">{review.type === "media" ? "Tip: pause the video where you want to change something, then tap the comment or mic button. The picture on screen goes with it." : null}{review.type === "media" || review.type === "long" ? null : <>Tip: select any words{review.type === "page" ? ", even on the page itself," : ""} to comment on them.</>}</div>
            {it.rec && <div className="lr-traycall"><RecText rec={it.rec} tail={it.facts?.length ? ` · ${it.facts.length} to check` : ""} /></div>}
            {callsFor(it)}
            <div className="lr-acts">
              <button className={`mic ${recording ? "rec" : ""}`} onClick={() => toggleMic()} aria-label={recording ? "Stop the voice note" : "Leave a voice note"}><MicIcon /></button>
              <button className="note" onClick={() => setComposing({ quote: "", itemId: it.id, x: 0, y: 0, touch: false, ...grab(it.id) })} aria-label="Leave a comment"><NoteIcon /> Leave a comment</button>
              {canDecide && <button className="chg" onClick={() => setReason({ reasons: [], note: "" })}>Request changes</button>}
              {canDecide && review.access && <button className="rej" onClick={() => send(it.id, { decision: "reject" })}>✕ Reject</button>}
              {canDecide && <button className="ok" onClick={() => send(it.id, { decision: "approve" })}>✓ Approve</button>}
            </div>
          </div>
        </div>
      )}

      {reason && it && (
        <div className="lr-ovl" onClick={(e) => e.target === e.currentTarget && setReason(null)}>
          <div className="lr-tray lr-reason" role="dialog" aria-label="What needs to change">
            <div className="top"><b>What needs to change in {it.n ?? cur + 1}?</b><button onClick={() => setReason(null)} aria-label="Close">✕</button></div>
            <div className="chips">{REASONS.map((r) => <button key={r} aria-pressed={reason.reasons.includes(r)} onClick={() => setReason({ ...reason, reasons: reason.reasons.includes(r) ? reason.reasons.filter((x) => x !== r) : [...reason.reasons, r] })}>{r}</button>)}</div>
            <label htmlFor="lr-note" style={{ fontSize: 12, color: "var(--muted)" }}>Or say it in your own words</label>
            <textarea id="lr-note" value={reason.note} onChange={(e) => setReason({ ...reason, note: e.target.value })} placeholder="e.g. Hold until Heidi confirms the PO box" />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="cancel" onClick={() => setReason(null)}>Back</button>
              <button className="send" disabled={!reason.reasons.length && !reason.note.trim()} onClick={() => send(it.id, { decision: "change", reasons: reason.reasons, note: reason.note })}>Send back for changes</button>
            </div>
          </div>
        </div>
      )}

      {jump && (
        <div className="lr-ovl" onClick={(e) => e.target === e.currentTarget && setJump(false)}>
          <div className="lr-tray lr-jumptray" role="dialog" aria-label="Jump to an item">
            <div className="top"><b>Jump to</b><button onClick={() => setJump(false)} aria-label="Close">✕</button></div>
            <div className="lr-jump">
              {(groups.length ? groups : [{ section: null, items: items.map((item, i) => ({ item, i })) }]).map((g, gi) => (
                <div key={g.section?.id || `loose-${gi}`} className="lr-jumpgrp" role="group" aria-label={g.section?.title || (groups.length ? "Not in a section" : "Items")}>
                  {groups.length > 0 && <div className="lr-jumpsec">{g.section?.title || "Not in a section"}</div>}
                  {g.items.map(({ item: x, i }) => {
                    const st = itemStatus(x, decisions[x.id]);
                    return (
                      <button key={x.id} className={i === cur ? "cur" : ""} onClick={() => { setJump(false); scrollTo(i); }}>
                        <span className="n">{x.n ?? i + 1}</span>
                        <span className="tx"><span className="tt">{x.title || x.id}</span>{x.date && <span className="sub">{x.date}</span>}</span>
                        {(st === "approved" || st === "changes") && <span className={`lr-badge ${st}`}>{BADGE[st]}</span>}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {mapOpen && <MapLightbox svg={mapOpen.svg} title={mapOpen.title} onClose={() => setMapOpen(null)} />}
      {toast && <div className="lr-toast" role="status"><span>{toast}</span></div>}
      {sharing && (
        <ShareWindow title={`Share “${review.title}”`} onClose={() => setSharing(false)} onToast={setToast}
          people={review.access ? { team: getTeam, load: () => getPeople(review.id), save: (l) => setPeople(review.id, l), me: review.me, roomUrl: `${window.location.origin}/reviews/${encodeURIComponent(review.id)}` } : null}
          link={ownsReview ? { load: async () => (await shareStatus(review.id)).token, turn: async (on) => (await shareLink(review.id, !on)).token, urlFor: shareUrl, blurb: "Anyone with the link can open this one review and leave notes, without signing in. Their notes show as “Share link”, not a name. It opens nothing else. Turn it off any time." } : null} />
      )}
      {picked && !composing && <CommentPill picked={picked} onOpen={() => { setComposing(picked); clearPicked(); }} />}
      {composing && <Composer quote={composing.quote} recording={recording} onSave={saveComment} frame={composing.frame} time={composing.time} onMic={() => toggleMic(composing.itemId, composing.quote || undefined, composing.anchor, { frame: composing.frame, time: composing.time })} onClose={() => setComposing(null)} />}
      {player && review && <ReadAlong key={player.audio} reviewId={review.id} audio={player.audio} times={player.times} label={player.label} onClose={stopBrief} />}
    </div>
    </WhoContext.Provider>
  );
}
